/* ============================================
   AUDIO CONTROLLER COMPONENT
   The Creative Orchestra - GEMINI Framework
   Floating audio control for ambient music
   ============================================ */

import { useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Volume2, 
  VolumeX, 
  Volume1, 
  Volume,
  Music,
  ChevronUp,
  ChevronDown,
} from 'lucide-react';
import { useAudio } from '@context/AudioContext';
import { useTheme } from '@context/ThemeContext';

/**
 * Audio Controller Component
 * Floating control for ambient orchestra music:
 * - Mute/unmute toggle
 * - Volume slider (on expand)
 * - Visual feedback for audio state
 * - Respects user preferences
 */
const AudioController = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { 
    isPlaying, 
    isMuted, 
    volume, 
    isLoaded, 
    toggleMute, 
    togglePlay,
    setVolumeLevel,
    increaseVolume,
    decreaseVolume,
  } = useAudio();
  const { activeTheme } = useTheme();

  // Get appropriate volume icon
  const VolumeIcon = useCallback(() => {
    if (isMuted || volume === 0) return VolumeX;
    if (volume < 0.3) return Volume;
    if (volume < 0.7) return Volume1;
    return Volume2;
  }, [isMuted, volume]);

  const Icon = VolumeIcon();
  const accentColor = activeTheme?.colors?.accent || '#818CF8';

  // Handle volume change
  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolumeLevel(newVolume);
  };

  return (
    <motion.div
      initial={{ opacity: 0, x: 50 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: 1, duration: 0.5 }}
      className="fixed bottom-6 right-6 z-50"
    >
      <AnimatePresence mode="wait">
        {isExpanded ? (
          <ExpandedController
            key="expanded"
            volume={volume}
            isMuted={isMuted}
            isPlaying={isPlaying}
            isLoaded={isLoaded}
            onVolumeChange={handleVolumeChange}
            onToggleMute={toggleMute}
            onTogglePlay={togglePlay}
            onClose={() => setIsExpanded(false)}
            accentColor={accentColor}
          />
        ) : (
          <CollapsedController
            key="collapsed"
            isMuted={isMuted}
            isPlaying={isPlaying}
            isLoaded={isLoaded}
            Icon={Icon}
            onToggleMute={toggleMute}
            onExpand={() => setIsExpanded(true)}
            accentColor={accentColor}
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

/**
 * Collapsed Controller - Just the mute button
 */
const CollapsedController = ({ 
  isMuted, 
  isPlaying, 
  isLoaded,
  Icon, 
  onToggleMute, 
  onExpand,
  accentColor,
}) => {
  return (
    <motion.div
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.8, opacity: 0 }}
      className="flex items-center gap-2"
    >
      {/* Expand button */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={onExpand}
        className="w-10 h-10 rounded-full bg-black/60 backdrop-blur-sm
          border border-white/10 flex items-center justify-center
          text-gray-400 hover:text-white hover:border-white/20
          transition-colors"
        aria-label="Expand audio controls"
      >
        <ChevronUp className="w-4 h-4" />
      </motion.button>

      {/* Main mute button */}
      <motion.button
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
        onClick={onToggleMute}
        disabled={!isLoaded}
        className={`
          relative w-12 h-12 rounded-full
          bg-black/60 backdrop-blur-sm border
          flex items-center justify-center
          transition-all duration-300
          ${!isLoaded 
            ? 'border-white/5 text-gray-600 cursor-not-allowed' 
            : isMuted
              ? 'border-white/10 text-gray-400 hover:text-white hover:border-white/20'
              : 'border-white/20 text-white'
          }
        `}
        style={{
          boxShadow: !isMuted && isPlaying 
            ? `0 0 20px ${accentColor}30` 
            : 'none',
        }}
        aria-label={isMuted ? 'Unmute' : 'Mute'}
      >
        <Icon className="w-5 h-5" />
        
        {/* Playing indicator */}
        {!isMuted && isPlaying && (
          <motion.div
            className="absolute inset-0 rounded-full border-2"
            style={{ borderColor: accentColor }}
            animate={{
              scale: [1, 1.2, 1],
              opacity: [0.5, 0, 0.5],
            }}
            transition={{
              duration: 2,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          />
        )}
      </motion.button>
    </motion.div>
  );
};

/**
 * Expanded Controller - Full volume slider
 */
const ExpandedController = ({ 
  volume, 
  isMuted, 
  isPlaying,
  isLoaded,
  onVolumeChange, 
  onToggleMute,
  onTogglePlay,
  onClose,
  accentColor,
}) => {
  // Get volume icon
  const getVolumeIcon = () => {
    if (isMuted || volume === 0) return VolumeX;
    if (volume < 0.3) return Volume;
    if (volume < 0.7) return Volume1;
    return Volume2;
  };

  const Icon = getVolumeIcon();

  return (
    <motion.div
      initial={{ scale: 0.8, opacity: 0, y: 20 }}
      animate={{ scale: 1, opacity: 1, y: 0 }}
      exit={{ scale: 0.8, opacity: 0, y: 20 }}
      className="bg-black/80 backdrop-blur-xl rounded-2xl border border-white/10
        p-4 shadow-2xl min-w-[200px]"
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Music className="w-4 h-4 text-gray-400" />
          <span className="text-sm font-medium text-white">Ambient Music</span>
        </div>
        <button
          onClick={onClose}
          className="p-1 rounded-full hover:bg-white/10 transition-colors"
          aria-label="Close"
        >
          <ChevronDown className="w-4 h-4 text-gray-400" />
        </button>
      </div>

      {/* Status */}
      <div className="flex items-center gap-2 mb-4">
        <div 
          className={`w-2 h-2 rounded-full ${
            !isLoaded 
              ? 'bg-yellow-500' 
              : isPlaying && !isMuted 
                ? 'bg-green-500 animate-pulse' 
                : 'bg-gray-500'
          }`}
        />
        <span className="text-xs text-gray-400">
          {!isLoaded ? 'Loading...' : isPlaying && !isMuted ? 'Playing' : 'Paused'}
        </span>
      </div>

      {/* Volume Slider */}
      <div className="flex items-center gap-3 mb-4">
        <button
          onClick={onToggleMute}
          disabled={!isLoaded}
          className="p-2 rounded-lg hover:bg-white/10 transition-colors
            text-gray-400 hover:text-white disabled:opacity-50"
          aria-label={isMuted ? 'Unmute' : 'Mute'}
        >
          <Icon className="w-5 h-5" />
        </button>

        <div className="flex-grow relative">
          <input
            type="range"
            min="0"
            max="1"
            step="0.05"
            value={isMuted ? 0 : volume}
            onChange={onVolumeChange}
            disabled={!isLoaded}
            className="w-full h-1 bg-white/10 rounded-full appearance-none cursor-pointer
              disabled:opacity-50 disabled:cursor-not-allowed
              [&::-webkit-slider-thumb]:appearance-none
              [&::-webkit-slider-thumb]:w-3
              [&::-webkit-slider-thumb]:h-3
              [&::-webkit-slider-thumb]:rounded-full
              [&::-webkit-slider-thumb]:bg-white
              [&::-webkit-slider-thumb]:cursor-pointer
              [&::-webkit-slider-thumb]:transition-transform
              [&::-webkit-slider-thumb]:hover:scale-125"
            style={{
              background: `linear-gradient(to right, ${accentColor} 0%, ${accentColor} ${(isMuted ? 0 : volume) * 100}%, rgba(255,255,255,0.1) ${(isMuted ? 0 : volume) * 100}%, rgba(255,255,255,0.1) 100%)`,
            }}
          />
        </div>

        <span className="text-xs text-gray-400 w-8 text-right">
          {Math.round((isMuted ? 0 : volume) * 100)}%
        </span>
      </div>

      {/* Info */}
      <p className="text-xs text-gray-500 text-center">
        🎵 Orchestra Ambient
      </p>
    </motion.div>
  );
};

export default AudioController;
